import React, { FunctionComponent, useMemo } from 'react'
import styled from '@emotion/styled'
import { useDropzone } from 'react-dropzone'
import { noop } from 'lodash-es'
import { connect } from 'react-redux'
import { NotificationActions, NotificationType } from 'store/notification'
import { Translation } from 'locale'
import { Dispatch } from '@reduxjs/toolkit'

interface DropzoneProps {
  className?: string
  onHandleError?: (err: unknown) => void
  accept?: string
  allowClick?: boolean
  render: (onClick?: (ect: React.MouseEvent) => unknown) => JSX.Element
  onDropFiles: (files: File[]) => unknown
  onPaste?: (clipboard: string) => unknown
  dataTestId?: string
}

const DropzoneContainer = styled.div`
  flex: auto;
  display: flex;
  flex-direction: row;
  min-width: 0;

  &:focus {
    outline: none;
  }
`

function isEventTargetAnInput(evt: React.ClipboardEvent<HTMLDivElement>) {
  return (evt?.nativeEvent?.target as HTMLElement)?.nodeName === 'INPUT'
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onHandleError: (err: unknown) =>
    dispatch(
      NotificationActions.showGrowl({
        type: NotificationType.Error,
        title: Translation.TC_CLIPBOARD_ERROR_TITLE,
        message: Translation.SC_CLIPBOARD_ERROR_MESSAGE,
        messageArgs: err
      })
    )
})

export const DropzoneComponent: FunctionComponent<DropzoneProps> = props => {
  const { render, className, dataTestId, allowClick, onDropFiles, onPaste, accept = '.csv, .xls, .xlsx' } = props
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept,
    noClick: !allowClick,
    onDropAccepted: onDropFiles
  })

  const style = useMemo(
    () => ({
      ...(isDragActive ? { opacity: 0.6 } : {})
    }),
    [isDragActive]
  )
  const rootProps = getRootProps({ style })

  const handlePaste = (evt: React.ClipboardEvent<HTMLDivElement>) => {
    if (isEventTargetAnInput(evt)) return

    if (onPaste) {
      try {
        // In case the user pastes a file via clipboard, we handle the paste differently
        if (evt.clipboardData.files.length !== 0 && evt.clipboardData.getData('text') === '') {
          onDropFiles([evt.clipboardData.files[0]])
        } else {
          onPaste(evt.clipboardData.getData('text'))
        }
      } catch (err) {
        props?.onHandleError?.(err)
      }
    }
  }

  return (
    <DropzoneContainer
      className={className}
      {...rootProps}
      onClick={noop}
      onPaste={handlePaste}
      role={'dropzone'}
      aria-label="Dropzone"
      data-test-id={dataTestId}>
      <input {...getInputProps()} />
      {render(rootProps.onClick)}
    </DropzoneContainer>
  )
}

export const Dropzone = connect(null, mapDispatchToProps)(DropzoneComponent)
